// mds-dojo — dijit/TooltipDialog — FAITHFUL port of references/dijit/TooltipDialog.js.
// Pops up a dialog that appears like a Tooltip. Extends layout/ContentPane +
// _TemplatedMixin + form/_FormMixin + _DialogMixin. AMD → ESM + declare from the
// faithful runtime. Unused dojo/has (only guarded the dropped dojo-bidi block) and
// dijit/main (side-effect only) imports dropped.
import declare from '../dojo/_base/declare-runtime';
import domClass from '../dojo/dom-class';
import keys from '../dojo/keys';
import lang from '../dojo/_base/lang';
import on from '../dojo/on';
import focus from './focus';
import ContentPane from './layout/ContentPane';
import _DialogMixin from './_DialogMixin';
import _FormMixin from './form/_FormMixin';
import _TemplatedMixin from './_TemplatedMixin';
import template from './templates/TooltipDialog.html?raw';

var TooltipDialog: any = declare('dijit.TooltipDialog',
  [ContentPane, _TemplatedMixin, _FormMixin, _DialogMixin], {
    // summary:
    //		Pops up a dialog that appears like a Tooltip

    // title: String
    //		Description of tooltip dialog (required for a11y)
    title: '',

    // doLayout: [protected] Boolean
    //		Don't change this parameter from the default value.
    doLayout: false,

    // autofocus: Boolean
    autofocus: true,

    // baseClass: [protected] String
    baseClass: 'dijitTooltipDialog',

    // _firstFocusItem: [private readonly] DomNode
    _firstFocusItem: null,

    // _lastFocusItem: [private readonly] DomNode
    _lastFocusItem: null,

    templateString: template,

    _setTitleAttr: 'containerNode',

    postCreate: function (this: any) {
      this.inherited(arguments);
      this.own(on(this.domNode, 'keydown', lang.hitch(this, '_onKey')));
    },

    orient: function (this: any, /*DomNode*/ node: any, /*String*/ aroundCorner: any, /*String*/ tooltipCorner: any) {
      // summary:
      //		Configure widget to be displayed in given position relative to the button.

      var newC = ({
        // Real around node
        'MR-ML': 'dijitTooltipRight',
        'ML-MR': 'dijitTooltipLeft',
        'TM-BM': 'dijitTooltipAbove',
        'BM-TM': 'dijitTooltipBelow',
        'BL-TL': 'dijitTooltipBelow dijitTooltipABLeft',
        'TL-BL': 'dijitTooltipAbove dijitTooltipABLeft',
        'BR-TR': 'dijitTooltipBelow dijitTooltipABRight',
        'TR-BR': 'dijitTooltipAbove dijitTooltipABRight',
        'BR-BL': 'dijitTooltipRight',
        'BL-BR': 'dijitTooltipLeft',

        // Positioning "around" a point, ex: mouse position
        'BR-TL': 'dijitTooltipBelow dijitTooltipABLeft',
        'BL-TR': 'dijitTooltipBelow dijitTooltipABRight',
        'TL-BR': 'dijitTooltipAbove dijitTooltipABRight',
        'TR-BL': 'dijitTooltipAbove dijitTooltipABLeft'
      } as any)[aroundCorner + '-' + tooltipCorner];

      domClass.replace(this.domNode, newC, this._currentOrientClass || '');
      this._currentOrientClass = newC;
    },

    focus: function (this: any) {
      // summary:
      //		Focus on first field
      this._getFocusItems();
      focus.focus(this._firstFocusItem);
    },

    onOpen: function (this: any, /*Object*/ pos: any) {
      // summary:
      //		Called when dialog is displayed.

      this.orient(this.domNode, pos.aroundCorner, pos.corner);

      // Position the tooltip connector for middle alignment.
      var aroundNodeCoords = pos.aroundNodePos;
      if (pos.corner.charAt(0) == 'M' && pos.aroundCorner.charAt(0) == 'M') {
        this.connectorNode.style.top = aroundNodeCoords.y + ((aroundNodeCoords.h - this.connectorNode.offsetHeight) >> 1) - pos.y + 'px';
        this.connectorNode.style.left = '';
      } else if (pos.corner.charAt(1) == 'M' && pos.aroundCorner.charAt(1) == 'M') {
        this.connectorNode.style.left = aroundNodeCoords.x + ((aroundNodeCoords.w - this.connectorNode.offsetWidth) >> 1) - pos.x + 'px';
      }

      this._onShow(); // lazy load trigger
    },

    onClose: function (this: any) {
      // summary:
      //		Called when dialog is hidden.
      this.onHide();
    },

    _onKey: function (this: any, /*Event*/ evt: any) {
      // summary:
      //		Handler for keydown events

      if (evt.keyCode == keys.ESCAPE) {
        // Use defer to avoid crash on IE, see #10396.
        this.defer('onCancel');
        evt.stopPropagation();
        evt.preventDefault();
      } else if (evt.keyCode == keys.TAB) {
        var node = evt.target;
        this._getFocusItems();
        if (this._firstFocusItem == this._lastFocusItem) {
          evt.stopPropagation();
          evt.preventDefault();
        } else if (node == this._firstFocusItem && evt.shiftKey) {
          focus.focus(this._lastFocusItem); // send focus to last item in dialog
          evt.stopPropagation();
          evt.preventDefault();
        } else if (node == this._lastFocusItem && !evt.shiftKey) {
          focus.focus(this._firstFocusItem); // send focus to first item in dialog
          evt.stopPropagation();
          evt.preventDefault();
        } else {
          // we want the browser's default tab handling to move focus
          // but we don't want the tab to propagate upwards
          evt.stopPropagation();
        }
      }
    }
  });

export default TooltipDialog;
